'use client'

import { useCallback, useEffect, useRef } from 'react'

import { logger } from '~/debug/logger'

import {
  useCreateConversationMutation,
  type AssistantContextRef,
  type AssistantPageContext,
  type AssistantSendMessageParams
} from '~/data/api/client'
import { runAssistantAction, streamAssistantActionProgress, streamAssistantMessage } from './api'
import type { AssistantStreamEvent } from './streamTypes'

type SendInput = {
  organizationId: string
  message: string
  contextRefs: AssistantContextRef[]
  pageContext: AssistantPageContext
  autoApprove: boolean
  skillId?: string
  /** Ids of attachments already uploaded to the active conversation. */
  attachmentIds?: AssistantSendMessageParams['attachmentIds']
  onEvent: (event: AssistantStreamEvent) => void
}

export default function useAssistantStream(currentOrgId: string | undefined) {
  const conversationIdRef = useRef<string | null>(null)
  const lastOrgIdRef = useRef<string | undefined>(currentOrgId)
  const streamAbortRef = useRef<AbortController | null>(null)
  const actionStreamAbortRef = useRef<AbortController | null>(null)
  const pendingCreateRef = useRef<Promise<string> | null>(null)
  const [createConversation] = useCreateConversationMutation()

  useEffect(() => {
    if (lastOrgIdRef.current !== currentOrgId) {
      streamAbortRef.current?.abort()
      streamAbortRef.current = null
      conversationIdRef.current = null
      pendingCreateRef.current = null
      lastOrgIdRef.current = currentOrgId
    }
  }, [currentOrgId])

  // Closing the composer (or navigating away) remounts this hook; abort so the next send cannot
  // overlap a conversation that is still receiving the previous turn.
  useEffect(() => {
    return () => {
      streamAbortRef.current?.abort()
      streamAbortRef.current = null
      actionStreamAbortRef.current?.abort()
      actionStreamAbortRef.current = null
    }
  }, [])

  /**
   * Returns the active conversation id, creating the conversation first when there is none. Attachments must be
   * uploaded to an existing conversation, so callers use this before the first message; concurrent calls share
   * one create request.
   */
  const ensureConversation = useCallback(
    async (organizationId: string): Promise<string> => {
      if (conversationIdRef.current) return conversationIdRef.current
      if (!pendingCreateRef.current) {
        const createPromise = createConversation({
          'X-Organization-Id': organizationId,
          assistantConversationCreateParams: {}
        })
          .unwrap()
          .then((conversation) => {
            if (!conversation.id) {
              throw new Error('Conversation response missing id')
            }
            // New chat / resume / org switch discard this create; do not revive it.
            if (pendingCreateRef.current === createPromise) {
              conversationIdRef.current = conversation.id
            }
            return conversation.id
          })
          .finally(() => {
            if (pendingCreateRef.current === createPromise) {
              pendingCreateRef.current = null
            }
          })
        pendingCreateRef.current = createPromise
      }
      return pendingCreateRef.current
    },
    [createConversation]
  )

  const sendMessage = useCallback(
    async (input: SendInput) => {
      if (streamAbortRef.current) {
        streamAbortRef.current.abort()
      }

      const abortController = new AbortController()
      streamAbortRef.current = abortController

      try {
        const conversationId = await ensureConversation(input.organizationId)
        if (abortController.signal.aborted) {
          input.onEvent({ type: 'cancelled' })
          return
        }

        await streamAssistantMessage({
          conversationId,
          organizationId: input.organizationId,
          message: input.message,
          contextRefs: input.contextRefs,
          pageContext: input.pageContext,
          autoApprove: input.autoApprove,
          skillId: input.skillId,
          attachmentIds: input.attachmentIds,
          signal: abortController.signal,
          onEvent: input.onEvent
        })
      } catch (error: unknown) {
        if (error instanceof Error && error.name === 'AbortError') {
          input.onEvent({ type: 'cancelled' })
          return
        }

        conversationIdRef.current = null
        logger.error('Assistant stream failed', error)
        input.onEvent({
          type: 'error',
          message: error instanceof Error ? error.message : 'Assistant stream failed'
        })
      }
    },
    [ensureConversation]
  )

  // Run an approved action and stream the assistant's follow-up turn (success
  // confirmation, or a diagnosis + corrected re-proposal on failure).
  const runAction = useCallback(
    async (actionId: string, onEvent: (event: AssistantStreamEvent) => void) => {
      if (!currentOrgId) {
        throw new Error('No organization selected')
      }
      if (streamAbortRef.current) {
        streamAbortRef.current.abort()
      }
      const abortController = new AbortController()
      streamAbortRef.current = abortController
      try {
        await runAssistantAction({
          organizationId: currentOrgId,
          actionId,
          signal: abortController.signal,
          onEvent
        })
      } catch (error: unknown) {
        if (error instanceof Error && error.name === 'AbortError') {
          onEvent({ type: 'cancelled' })
          return
        }
        logger.error('Assistant action run failed', error)
        onEvent({
          type: 'error',
          message: error instanceof Error ? error.message : 'Action run failed'
        })
      }
    },
    [currentOrgId]
  )

  const streamActionProgress = useCallback(
    async (actionId: string, onEvent: (event: AssistantStreamEvent) => void) => {
      if (!currentOrgId) {
        throw new Error('No organization selected')
      }
      actionStreamAbortRef.current?.abort()
      const abortController = new AbortController()
      actionStreamAbortRef.current = abortController
      try {
        await streamAssistantActionProgress({
          organizationId: currentOrgId,
          actionId,
          signal: abortController.signal,
          onEvent
        })
      } catch (error: unknown) {
        if (error instanceof Error && error.name === 'AbortError') {
          onEvent({ type: 'cancelled' })
          return
        }
        logger.error('Assistant action progress stream failed', error)
        onEvent({
          type: 'error',
          message: error instanceof Error ? error.message : 'Action progress stream failed'
        })
      }
    },
    [currentOrgId]
  )

  const abort = useCallback(() => {
    streamAbortRef.current?.abort()
    streamAbortRef.current = null
    actionStreamAbortRef.current?.abort()
    actionStreamAbortRef.current = null
  }, [])

  const resetConversation = useCallback(() => {
    streamAbortRef.current?.abort()
    streamAbortRef.current = null
    actionStreamAbortRef.current?.abort()
    actionStreamAbortRef.current = null
    conversationIdRef.current = null
    pendingCreateRef.current = null
  }, [])

  // Reopen an existing conversation from history: point the next turn at its id
  // (no new conversation is created) so follow-up messages append to it.
  const resumeConversation = useCallback((conversationId: string) => {
    streamAbortRef.current?.abort()
    streamAbortRef.current = null
    conversationIdRef.current = conversationId
    pendingCreateRef.current = null
  }, [])

  const getActiveConversationId = useCallback(() => conversationIdRef.current, [])

  return {
    sendMessage,
    ensureConversation,
    runAction,
    streamActionProgress,
    abort,
    resetConversation,
    resumeConversation,
    getActiveConversationId
  }
}
